import { FC } from 'react';
import styled from 'styled-components';
import { useUserLanguage } from '@/customHooks';
import { PageSizeOption } from '@/graphql/generated';

type PaginationProps = {
  currentPage: number;
  onPageChange: (page: number) => void;
  currentDataCount: number;
  pageSize: PageSizeOption | null;
  totalPages?: number;
};

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  margin-bottom: 1rem;
`;

const PageInfo = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`;

const IconButton = styled.button<{ $disabled: boolean }>`
  background-color: transparent;
  border: none;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $disabled }) => $disabled ? 0.5 : 1};
  
  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.light};
    border-radius: ${({ theme }) => theme.border.radius.sm};
  }
  
  &:disabled {
    cursor: not-allowed;
  }
  
  svg {
    width: 20px;
    height: 20px;
    fill: ${({ theme }) => theme.colors.text};
  }
`;

const ChevronLeftIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0"/>
  </svg>
);

const ChevronRightIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708"/>
  </svg>
);

// Mapeo de PageSizeOption a número
const getPageSizeNumber = (pageSize: PageSizeOption | null): number => {
  if (!pageSize) return Infinity; // Si no hay pageSize, no hay límite
  
  switch (pageSize) {
    case PageSizeOption.TwentyFive:
      return 25;
    case PageSizeOption.Fifty:
      return 50;
    case PageSizeOption.SeventyFive:
      return 75;
    case PageSizeOption.OneHundred:
      return 100;
    default:
      return Infinity;
  }
};

export const Pagination: FC<PaginationProps> = ({ 
  currentPage, 
  onPageChange, 
  currentDataCount, 
  pageSize,
  totalPages 
}) => {
  const userLanguage = useUserLanguage();

  const textsData = {
    en: {
      previous: 'Previous',
      next: 'Next',
      page: 'Page',
      of: 'of',
    },
    es: {
      previous: 'Anterior',
      next: 'Siguiente',
      page: 'Página',
      of: 'de',
    }
  };

  const texts = userLanguage === 'es' ? textsData.es : textsData.en;
  
  const pageSizeNumber = getPageSizeNumber(pageSize);
  // Si tenemos totalPages del backend, usamos eso; si no, calculamos si hay más datos
  const hasMoreData = totalPages ? currentPage < totalPages : currentDataCount >= pageSizeNumber;

  return (
    <PaginationContainer>
      <IconButton
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        $disabled={currentPage === 1}
        type="button"
      >
        {ChevronLeftIcon}
      </IconButton>
      
      <PageInfo>
        {texts.page} {currentPage}{totalPages ? ` ${texts.of} ${totalPages}` : ''}
      </PageInfo>

      <IconButton
        onClick={() => onPageChange(currentPage + 1)}
        disabled={!hasMoreData || currentDataCount === 0}
        $disabled={!hasMoreData || currentDataCount === 0}
        type="button"
      >
        {ChevronRightIcon}
      </IconButton>
    </PaginationContainer>
  );
};

